// Library imports
import { useEffect, useRef, useState } from "react";

// User Component imports
import { VIDEO_ACCEPT, isSupportedVideoFile, checkVideoHasAudio } from "../utils/videoFile";
import { isOptimizable } from "./optimize/videoOptimizeSpec";
import { createVideoEncoder } from "./optimize/videoEncoder";

// サーバーが受け付ける拡張子（.mov は最適化して .mp4 にしないと送れない）
const UPLOADABLE_EXTS = ['.mp4', '.webm'];

const isUploadableAsIs = (file) => {
    const dot = file.name.lastIndexOf('.');
    return dot > 0 && UPLOADABLE_EXTS.includes(file.name.slice(dot).toLowerCase());
};

// ウィジェット表示動画は idle を縮小したもの。idle から作るときの元ファイル（名前だけ _mini を付ける）
const MINI_KEY = 'idle_mini';
const miniSourceOf = (idle) => {
    const dot = idle.name.lastIndexOf('.');
    const name = dot > 0 ? `${idle.name.slice(0, dot)}_mini${idle.name.slice(dot)}` : `${idle.name}_mini`;
    return new File([idle], name, { type: idle.type, lastModified: idle.lastModified });
};

/**
 * 種別ごとの添付動画を管理する（形式チェック・ブラウザでの最適化・音声トラック検出・プレビューURL）
 * files[key] = {
 *   source, name, sourceSize, bad, canOptimize, mustOptimize, optimize,
 *   status: 'queued' | 'optimizing' | 'done' | 'skipped' | 'error', progress, info, errorCode, error,
 *   file, size,   // 送信するファイル（最適化後、または最適化しない元ファイル）。用意できるまで null
 *   hasAudio, previewUrl,
 *   auto,         // idle から自動で作ったウィジェット表示動画
 * }
 * 最適化はモーダルごとに1つのエンコーダ（Worker の集まり）を全種別で共有し、区間を順に流す
 * autoMini が ON の間は、ウィジェット表示動画を自分で添付していなければ idle から作る
 */
export const useVideoAttachments = () => {
    const [files, setFiles] = useState({});
    const [autoMini, setAutoMiniState] = useState(true);
    const filesRef = useRef(files);
    filesRef.current = files;
    const urlsRef = useRef({});
    const tokensRef = useRef({});
    const seqRef = useRef(0);
    const jobsRef = useRef({});
    const encoderRef = useRef(null);
    const mountedRef = useRef(true);

    const revoke = (key) => {
        if (urlsRef.current[key]) {
            URL.revokeObjectURL(urlsRef.current[key]);
            delete urlsRef.current[key];
        }
    };

    const setPreview = (key, file) => {
        revoke(key);
        urlsRef.current[key] = URL.createObjectURL(file);
        return urlsRef.current[key];
    };

    // 添付し直されていなければ（世代が同じなら）その種別の状態を書き換える
    const update = (key, token, patch) => {
        setFiles((prev) => (prev[key] && tokensRef.current[key] === token ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
    };

    const stopJob = (key) => {
        const job = jobsRef.current[key];
        if (!job) return;
        delete jobsRef.current[key];
        job.cancel();
    };

    // 区間がエンコーダの順番待ちの間は queued、1つでも始まったら optimizing
    const enqueue = (key, token, source) => {
        if (!mountedRef.current) return;
        if (!encoderRef.current) encoderRef.current = createVideoEncoder();
        const { promise, cancel } = encoderRef.current.encode(source, key, {
            onStart: () => update(key, token, { status: 'optimizing' }),
            onProgress: (progress) => update(key, token, { progress }),
        });
        const job = { token, cancel };
        jobsRef.current[key] = job;
        promise
            .then(({ file, info }) => {
                if (tokensRef.current[key] !== token) return;
                const previewUrl = setPreview(key, file);
                update(key, token, { status: 'done', progress: 1, file, size: file.size, info, previewUrl });
            }, (e) => {
                if (e.code === 'cancelled' || tokensRef.current[key] !== token) return;
                update(key, token, { status: 'error', errorCode: e.code, error: e.message });
            })
            .finally(() => {
                if (jobsRef.current[key] === job) delete jobsRef.current[key];
            });
    };

    // 最適化しない（元ファイルをそのまま送る）ときは音声トラックの有無を調べて注意を出す
    const checkAudio = (key, token, source) => {
        checkVideoHasAudio(source).then((hasAudio) => update(key, token, { hasAudio }));
    };

    // 閉じたら変換を止め、作ったプレビューURLを解放する
    useEffect(() => {
        mountedRef.current = true;
        const urls = urlsRef.current;
        return () => {
            mountedRef.current = false;
            Object.values(jobsRef.current).forEach((job) => job.cancel());
            jobsRef.current = {};
            if (encoderRef.current) encoderRef.current.dispose();
            encoderRef.current = null;
            Object.keys(urls).forEach((key) => URL.revokeObjectURL(urls[key]));
        };
    }, []);

    const attachEntry = (key, source, auto) => {
        stopJob(key);
        const token = ++seqRef.current;
        tokensRef.current[key] = token;
        const bad = !isSupportedVideoFile(source);
        const canOptimize = !bad && isOptimizable(source);
        // idle から作る mini は縮小が目的なので「最適化しない」を選べない
        const mustOptimize = canOptimize && (auto || !isUploadableAsIs(source));
        const previewUrl = bad ? null : setPreview(key, source);
        if (bad) revoke(key);
        const base = { source, name: source.name, sourceSize: source.size, bad, canOptimize, mustOptimize, auto, hasAudio: undefined, previewUrl, progress: 0, info: null, errorCode: null, error: null };
        if (canOptimize) {
            setFiles((prev) => ({ ...prev, [key]: { ...base, optimize: true, status: 'queued', file: null, size: null } }));
            enqueue(key, token, source);
            return;
        }
        setFiles((prev) => ({ ...prev, [key]: { ...base, optimize: false, status: 'skipped', file: bad ? null : source, size: bad ? null : source.size } }));
        if (!bad) checkAudio(key, token, source);
    };

    const removeEntry = (key) => {
        stopJob(key);
        delete tokensRef.current[key];
        revoke(key);
        setFiles((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    // idle に合わせて自動の mini を作り直す／外す（自分で添付した mini には触らない）
    const syncAutoMini = (idle, enabled) => {
        const mini = filesRef.current[MINI_KEY];
        if (mini && !mini.auto) return;
        if (enabled && idle && isSupportedVideoFile(idle) && isOptimizable(idle)) {
            attachEntry(MINI_KEY, miniSourceOf(idle), true);
        } else if (mini) {
            removeEntry(MINI_KEY);
        }
    };

    const attach = (key, source) => {
        if (!source) return;
        attachEntry(key, source, false);
        if (key === 'idle') syncAutoMini(source, autoMini);
    };

    const setAutoMini = (enabled) => {
        setAutoMiniState(enabled);
        const idle = filesRef.current.idle;
        syncAutoMini(idle && !idle.bad ? idle.source : null, enabled);
    };

    const setOptimize = (key, optimize) => {
        const entry = files[key];
        if (!entry || !entry.canOptimize || entry.optimize === optimize) return;
        if (!optimize && entry.mustOptimize) return;
        stopJob(key);
        const token = ++seqRef.current;
        tokensRef.current[key] = token;
        const previewUrl = setPreview(key, entry.source);
        const reset = { optimize, progress: 0, info: null, errorCode: null, error: null, hasAudio: undefined, previewUrl };
        if (optimize) {
            setFiles((prev) => ({ ...prev, [key]: { ...prev[key], ...reset, status: 'queued', file: null, size: null } }));
            enqueue(key, token, entry.source);
            return;
        }
        setFiles((prev) => ({ ...prev, [key]: { ...prev[key], ...reset, status: 'skipped', file: entry.source, size: entry.source.size } }));
        checkAudio(key, token, entry.source);
    };

    const clear = (key) => {
        const mini = filesRef.current[MINI_KEY];
        removeEntry(key);
        if (key === 'idle' && mini && mini.auto) removeEntry(MINI_KEY);
        // 自動の mini を取り消したら、自動で作るのをやめる
        if (key === MINI_KEY && mini && mini.auto) setAutoMiniState(false);
    };

    // 添付を全部外して、idle から mini を作る設定も最初の状態（ON）に戻す
    const clearAll = () => {
        Object.keys(filesRef.current).forEach(removeEntry);
        setAutoMiniState(true);
    };

    const pick = (key) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = VIDEO_ACCEPT;
        input.onchange = () => attach(key, input.files && input.files[0]);
        input.click();
    };

    const entries = Object.values(files);
    const busy = entries.some((f) => f.status === 'queued' || f.status === 'optimizing');
    const hasError = entries.some((f) => f.status === 'error');

    return { files, attach, clear, clearAll, pick, setOptimize, autoMini, setAutoMini, busy, hasError };
};

export default useVideoAttachments;
